<script setup lang="ts">
import type { Subscription } from '@entities/subscription'

import { BadgeOrderStatus } from '@shared/ui/badges'
import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { LinkTextPrimary } from '@shared/ui/links'

import SubscriptionOrderActions from './SubscriptionOrderActions.vue'
import { useSubscriptionPayments } from '../model/subscription-payments.hook'

defineOptions({
  name: 'SubscriptionPayments',
})

defineProps<{
  subscription: Subscription
  canManageOrders: boolean
}>()

const {
  getOrderUrl,
  getNumber,
  getTotal,
  getPaidAt,
} = useSubscriptionPayments()
</script>

<template>
  <AppTable
    class="rounded-table subscription-payments"
    :paginated="false"
  >
    <template #head>
      <AppTr>
        <AppTh class="text-center subscription-payments__id">
          <AppText
            name="body1-medium"
            color="slate-900"
          >
            #
          </AppText>
        </AppTh>
        <AppTh class="text-center subscription-payments__number">
          <AppText
            name="body1-medium"
            color="slate-900"
          >
            Payment #
          </AppText>
        </AppTh>
        <AppTh>
          <AppText
            name="body1-medium"
            color="slate-900"
          >
            Sum
          </AppText>
        </AppTh>
        <AppTh>
          <AppText
            name="body1-medium"
            color="slate-900"
          >
            Payment status
          </AppText>
        </AppTh>
        <AppTh>
          <AppText
            name="body1-medium"
            color="slate-900"
          >
            Payment date
          </AppText>
        </AppTh>
        <AppTh class="subscription-payments__actions">
          <span class="subscription-payments__sr-only">Actions</span>
        </AppTh>
      </AppTr>
    </template>

    <template
      v-if="subscription.orders.length"
      #body
    >
      <AppTr
        v-for="order in subscription.orders"
        :key="order.id"
      >
        <AppTd class="medium text-center">
          <LinkTextPrimary :href="getOrderUrl(order.id)">
            {{ order.id }}
          </LinkTextPrimary>
        </AppTd>
        <AppTd class="medium text-center">
          <AppText
            name="body1"
            color="slate-900"
          >
            {{ getNumber(order) }}
          </AppText>
        </AppTd>
        <AppTd class="medium subscription-payments__sum">
          <AppText
            name="body1"
            color="slate-900"
          >
            {{ getTotal(order) }}
          </AppText>
        </AppTd>
        <AppTd class="medium">
          <BadgeOrderStatus :status="order.status" />
        </AppTd>
        <AppTd class="medium">
          <AppText
            name="body1"
            color="slate-900"
          >
            {{ getPaidAt(order) }}
          </AppText>
        </AppTd>
        <AppTd class="zero-padding subscription-payments__actions">
          <SubscriptionOrderActions
            :order="order"
            :subscription="subscription"
            :can-manage="canManageOrders"
          />
        </AppTd>
      </AppTr>
    </template>
  </AppTable>
</template>

<style scoped lang="scss" src="./SubscriptionPayments.scss" />
